import { useFirstAudienceId } from '@/features/audiences/hooks/useFirstAudienceId';
import { useSmsTemplateConnector } from '@/features/campaigns/v2/Sms/hooks/useSmsTemplateConnector';
import { extractSmsTemplatesFromPrepareParams, SmsTemplate } from '@/features/campaigns/v2/Sms/Templates/smsTemplateListFlowMapper';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useCompanyValues } from '@/providers';
import { PrepareConnectorFlowStepResponse, usePrepareConnectorFlowStepMutation } from '@/services/endpoints/workspace-connectors';
import { translateI18nValues } from '@/utils/translationUtil';
import { useCallback, useEffect, useRef, useState } from 'react';

type PrepareStepResult = PrepareConnectorFlowStepResponse['configuration'] extends infer T
  ? T extends { elements?: { params?: infer Params } }
    ? NonNullable<Params>
    : Record<string, unknown>
  : Record<string, unknown>;

type UseSmsTemplatesOptions = {
  skip?: boolean;
};

export const useSmsTemplates = (options?: UseSmsTemplatesOptions) => {
  const skip = options?.skip ?? false;
  const workspace = useWorkspace();
  const { audienceId, isLoading: isAudienceLoading, isAudienceSettled } = useFirstAudienceId();
  const { currentLanguage } = useCompanyValues();
  const {
    listFlowId,
    editFlowId,
    deleteFlowId,
    connectorInstanceId,
    isListReady,
    isInsertReady,
    isEditReady,
    isDeleteReady,
    isLoading: isConnectorLoading,
    isError: isConnectorError,
    missingInstance,
  } = useSmsTemplateConnector();
  const [prepareStep] = usePrepareConnectorFlowStepMutation();

  const [templates, setTemplates] = useState<SmsTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [isError, setIsError] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const activeRequestId = useRef(0);

  const missingAudience = isAudienceSettled && !audienceId;
  const canFetch = isListReady || isEditReady || isDeleteReady;

  const prepareAndExtract = useCallback(
    async (flowId: string) => {
      const response = await prepareStep({
        workspaceId: workspace.id,
        flowId,
        audienceId: audienceId!,
        stepId: 'step1',
        stepData: {},
        connectorInstanceId,
      }).unwrap();

      const translated = translateI18nValues(response, currentLanguage);
      const params = translated?.configuration?.elements?.params as PrepareStepResult;
      return extractSmsTemplatesFromPrepareParams(params as Parameters<typeof extractSmsTemplatesFromPrepareParams>[0]);
    },
    [audienceId, connectorInstanceId, currentLanguage, prepareStep, workspace.id]
  );

  const fetchTemplates = useCallback(
    async (isRefetch = false) => {
      if (skip || !canFetch || !connectorInstanceId || !audienceId) {
        return;
      }

      const requestId = ++activeRequestId.current;

      try {
        if (isRefetch) {
          setIsFetching(true);
        } else {
          setIsLoading(true);
        }
        setIsError(false);

        let nextTemplates: SmsTemplate[] = [];

        if (listFlowId) {
          nextTemplates = await prepareAndExtract(listFlowId);
        }

        if (nextTemplates.length === 0 && editFlowId) {
          nextTemplates = await prepareAndExtract(editFlowId);
        }

        if (nextTemplates.length === 0 && deleteFlowId) {
          nextTemplates = await prepareAndExtract(deleteFlowId);
        }

        if (requestId !== activeRequestId.current) {
          return;
        }

        setTemplates(nextTemplates);
      } catch {
        if (requestId === activeRequestId.current) {
          setIsError(true);
        }
      } finally {
        if (requestId === activeRequestId.current) {
          setIsLoading(false);
          setIsFetching(false);
          setHasFetched(true);
        }
      }
    },
    [audienceId, canFetch, connectorInstanceId, deleteFlowId, editFlowId, listFlowId, prepareAndExtract, skip]
  );

  const reload = useCallback((): Promise<void> => fetchTemplates(true), [fetchTemplates]);

  useEffect(() => {
    if (skip) {
      setTemplates([]);
      setIsLoading(false);
      setIsFetching(false);
      setIsError(false);
      setHasFetched(false);
      return;
    }

    if (isConnectorLoading || isAudienceLoading) {
      return;
    }

    if (missingAudience) {
      setTemplates([]);
      setIsLoading(false);
      setIsFetching(false);
      setIsError(false);
      setHasFetched(true);
      return;
    }

    if (canFetch) {
      void fetchTemplates();
      return;
    }

    setIsLoading(false);
    setHasFetched(true);
  }, [canFetch, fetchTemplates, isAudienceLoading, isConnectorLoading, missingAudience, skip]);

  return {
    templates,
    templatesCount: templates.length,
    hasFetched,
    missingAudience,
    missingInstance,
    isLoading: skip ? false : isAudienceLoading || isConnectorLoading || (!missingAudience && (isLoading || !hasFetched)),
    isFetching,
    isError: isError || isConnectorError,
    reload,
    isListReady,
    isInsertReady,
    isEditReady,
    isDeleteReady,
    connectorInstanceId,
  };
};
